import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

import { API_ENDPOINT } from '../../constants';
import { fetchWrapper } from '../../helpers';

import type { ActionReducerMapBuilder, PayloadAction } from '@reduxjs/toolkit';
import type { IClientFamilyBackground, IParentCaregiver, ISibling } from '../../utils/types';

const name = 'clientFamilyBackgrounds';
const initialState = createInitialState();
const reducers = createReducers();
// SAFETY: Legacy thunk action creators use the store dispatch result contract.
const extraActions = createExtraActions() as any;
const slice = createSlice({
  name,
  initialState,
  reducers,
  extraReducers: (builder: ActionReducerMapBuilder<ClientFamilyBackgroundState>) =>
    createExtraReducers(builder),
});

export interface ClientFamilyBackgroundState {
  clientFamilyBackground: IClientFamilyBackground | null;
  originalClientFamilyBackground: IClientFamilyBackground | null;
  parentsCaregivers: Array<IParentCaregiver> | null;
  strengths: Array<string> | null;
  interests: Array<string> | null;
  siblings: Array<ISibling> | null;
  loading: boolean;
  error: any;
  parentCaregiverErrorCounts: number | null;
  siblingErrorCounts: number | null;
  memberlanguageErrorCount: number;
  strengthErrorCount: number;
  interestErrorCount: number;
  familyBackgroundErrorCount: number;
}

function createInitialState(): ClientFamilyBackgroundState {
  return {
    clientFamilyBackground: null,
    originalClientFamilyBackground: null,
    parentsCaregivers: [],
    siblings: null,
    strengths: [],
    interests: [],
    loading: false,
    error: null,
    parentCaregiverErrorCounts: null,
    siblingErrorCounts: null,
    memberlanguageErrorCount: 0,
    familyBackgroundErrorCount: 0,
    strengthErrorCount: 0,
    interestErrorCount: 0,
  };
}

function createReducers() {
  return {
    clearState,
    updateState,
  };

  function clearState(state: ClientFamilyBackgroundState) {
    state.clientFamilyBackground = null;
    state.originalClientFamilyBackground = null;
    state.parentsCaregivers = null;
    state.siblings = null;
    state.strengths = [];
    state.interests = [];
    state.loading = false;
    state.error = null;
    state.parentCaregiverErrorCounts = null;
    state.siblingErrorCounts = null;
    state.familyBackgroundErrorCount = 0;
    state.strengthErrorCount = 0;
    state.interestErrorCount = 0;
  }

  function updateState(state: ClientFamilyBackgroundState, action: any) {
    const { key, data } = action.payload;
    Object.assign(state, { [key]: data });
  }
}

function createExtraActions() {
  const baseUrl = `${API_ENDPOINT}/treatment_plans`;

  return {
    getClientFamilyBackground: getClientFamilyBackground(),
    createClientFamilyBackground: createClientFamilyBackground(),
    updateClientFamilyBackground: updateClientFamilyBackground(),
    removeClientFamilyBackground: removeClientFamilyBackground(),
    getParentCaregivers: getParentCaregivers(),
    createParentCaregiver: createParentCaregiver(),
    updateParentCaregiver: updateParentCaregiver(),
    removeParentCaregiver: removeParentCaregiver(),
    createSibling: createSibling(),
    updateSibling: updateSibling(),
    removeSibling: removeSibling(),
    updateStrengths: updateStrengths(),
    updateInterests: updateInterests(),
  };

  function getClientFamilyBackground() {
    return createAsyncThunk(
      `${name}/getClientFamilyBackground`,
      async ({ treatmentPlanId, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.get({
          url: `${baseUrl}/${treatmentPlanId}/client_and_family_background/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function createClientFamilyBackground() {
    return createAsyncThunk(
      `${name}/createClientFamilyBackground`,
      async ({ treatmentPlanId, data, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.post(
          {
            url: `${baseUrl}/${treatmentPlanId}/client_and_family_background/`,
            getAccessTokenSilently,
          },
          data,
        );
        return response;
      },
    );
  }

  function updateClientFamilyBackground() {
    return createAsyncThunk(
      `${name}/updateClientFamilyBackground`,
      async ({ treatmentPlanId, data, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.patch(
          {
            url: `${baseUrl}/${treatmentPlanId}/client_and_family_background/`,
            getAccessTokenSilently,
          },
          data,
        );
        return response;
      },
    );
  }

  function removeClientFamilyBackground() {
    return createAsyncThunk(
      `${name}/removeClientFamilyBackground`,
      async ({ treatmentPlanId, clientFamilyBackgroundId, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.delete({
          url: `${baseUrl}/${treatmentPlanId}/client_and_family_background/${clientFamilyBackgroundId}/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function getParentCaregivers() {
    return createAsyncThunk(
      `${name}/getParentCaregivers`,
      async ({ treatmentPlanId, backgroundId, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.get({
          url: `${baseUrl}/${treatmentPlanId}/client_and_family_background/${backgroundId}/parents_and_caregivers/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function createParentCaregiver() {
    return createAsyncThunk(
      `${name}/createParentCaregiver`,
      async ({ treatmentPlanId, backgroundId, data, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.post(
          {
            url: `${baseUrl}/${treatmentPlanId}/client_and_family_background/${backgroundId}/parents_and_caregivers/`,
            getAccessTokenSilently,
          },
          data,
        );
        return response;
      },
    );
  }

  function updateParentCaregiver() {
    return createAsyncThunk(
      `${name}/updateParentCaregiver`,
      async ({ treatmentPlanId, backgroundId, data, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.patch(
          {
            url: `${baseUrl}/${treatmentPlanId}/client_and_family_background/${backgroundId}/parents_and_caregivers/${data.id}/`,
            getAccessTokenSilently,
          },
          data,
        );
        return response;
      },
    );
  }

  function removeParentCaregiver() {
    return createAsyncThunk(
      `${name}/removeParentCaregiver`,
      async ({ treatmentPlanId, backgroundId, parentCaregiverId, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.delete({
          url: `${baseUrl}/${treatmentPlanId}/client_and_family_background/${backgroundId}/parents_and_caregivers/${parentCaregiverId}/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function createSibling() {
    return createAsyncThunk(
      `${name}/createSibling`,
      async ({ treatmentPlanId, backgroundId, data, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.post(
          {
            url: `${baseUrl}/${treatmentPlanId}/client_and_family_background/${backgroundId}/siblings/`,
            getAccessTokenSilently,
          },
          data,
        );
        return response;
      },
    );
  }

  function updateSibling() {
    return createAsyncThunk(
      `${name}/updateSibling`,
      async ({ treatmentPlanId, backgroundId, data, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.patch(
          {
            url: `${baseUrl}/${treatmentPlanId}/client_and_family_background/${backgroundId}/siblings/${data.id}/`,
            getAccessTokenSilently,
          },
          data,
        );
        return response;
      },
    );
  }

  function removeSibling() {
    return createAsyncThunk(
      `${name}/removeSibling`,
      async ({ treatmentPlanId, backgroundId, siblingId, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.delete({
          url: `${baseUrl}/${treatmentPlanId}/client_and_family_background/${backgroundId}/siblings/${siblingId}/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function updateStrengths() {
    return createAsyncThunk(
      `${name}/updateStrengths`,
      async ({ treatmentPlanId, data, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.put(
          {
            url: `${baseUrl}/${treatmentPlanId}/client_and_family_background/client_strengths/`,
            getAccessTokenSilently,
          },
          data,
        );
        return response;
      },
    );
  }

  function updateInterests() {
    return createAsyncThunk(
      `${name}/updateInterests`,
      async ({ treatmentPlanId, data, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.put(
          {
            url: `${baseUrl}/${treatmentPlanId}/client_and_family_background/client_interests/`,
            getAccessTokenSilently,
          },
          data,
        );
        return response;
      },
    );
  }
}

function createExtraReducers(builder: ActionReducerMapBuilder<ClientFamilyBackgroundState>) {
  const {
    getClientFamilyBackground,
    createClientFamilyBackground,
    updateClientFamilyBackground,
    removeClientFamilyBackground,
    getParentCaregivers,
    createParentCaregiver,
    updateParentCaregiver,
    removeParentCaregiver,
    createSibling,
    updateSibling,
    removeSibling,
    updateStrengths,
    updateInterests,
  } = extraActions;

  builder
    // getClientFamilyBackground()
    .addCase(getClientFamilyBackground.pending, (state: ClientFamilyBackgroundState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      getClientFamilyBackground.fulfilled,
      (state: ClientFamilyBackgroundState, action: any) => {
        state.loading = false;
        state.clientFamilyBackground = action.payload;
        state.originalClientFamilyBackground = action.payload;
        state.parentsCaregivers =
          action.payload && Object.hasOwn(action.payload, 'parents_and_caregivers')
            ? action.payload.parents_and_caregivers
            : [];
        state.siblings =
          action.payload && Object.hasOwn(action.payload, 'siblings')
            ? action.payload.siblings
            : [];
        state.strengths =
          action.payload && Object.hasOwn(action.payload, 'client_strengths')
            ? action.payload.client_strengths
            : [];
        state.interests =
          action.payload && Object.hasOwn(action.payload, 'client_interests')
            ? action.payload.client_interests
            : [];
      },
    )
    .addCase(
      getClientFamilyBackground.rejected,
      (state: ClientFamilyBackgroundState, action: any) => {
        state.error = action.error;
        state.loading = false;
      },
    )

    // createClientFamilyBackground()
    .addCase(createClientFamilyBackground.pending, (state: ClientFamilyBackgroundState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      createClientFamilyBackground.fulfilled,
      (state: ClientFamilyBackgroundState, action: any) => {
        state.loading = false;
        state.clientFamilyBackground = action.payload;
        state.originalClientFamilyBackground = action.payload;
        state.parentsCaregivers = [];
        state.siblings = [];
      },
    )
    .addCase(
      createClientFamilyBackground.rejected,
      (state: ClientFamilyBackgroundState, action: any) => {
        state.error = action.error;
        state.loading = false;
        state.parentsCaregivers = [];
        state.siblings = [];
        toast.error(state.error.message);
      },
    )

    // updateClientFamilyBackground()
    .addCase(updateClientFamilyBackground.pending, (state: ClientFamilyBackgroundState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      updateClientFamilyBackground.fulfilled,
      (state: ClientFamilyBackgroundState, action: any) => {
        state.loading = false;

        state.clientFamilyBackground = action.payload;
        state.originalClientFamilyBackground = action.payload;
        state.parentsCaregivers = state.parentsCaregivers
          ? state.parentsCaregivers.length === 0
            ? action.payload.parents_and_caregivers
            : state.parentsCaregivers
          : action.payload.parents_and_caregivers;

        state.siblings = state.siblings
          ? state.siblings.length === 0
            ? action.payload.siblings
            : state.siblings
          : action.payload.siblings;
      },
    )
    .addCase(
      updateClientFamilyBackground.rejected,
      (state: ClientFamilyBackgroundState, action: any) => {
        state.error = action.error;
        state.loading = false;
        toast.error(state.error.message);
      },
    )

    // removeClientFamilyBackground()
    .addCase(removeClientFamilyBackground.pending, (state: ClientFamilyBackgroundState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(removeClientFamilyBackground.fulfilled, (state: ClientFamilyBackgroundState) => {
      state.loading = false;
      state.clientFamilyBackground = null;
      state.originalClientFamilyBackground = null;
      state.parentsCaregivers = [];
      state.siblings = [];
    })
    .addCase(
      removeClientFamilyBackground.rejected,
      (state: ClientFamilyBackgroundState, action: any) => {
        state.error = action.error;
        state.loading = false;
        toast.error(state.error.message);
      },
    )

    // getParentCaregivers()
    .addCase(getParentCaregivers.pending, (state: ClientFamilyBackgroundState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      getParentCaregivers.fulfilled,
      (state: ClientFamilyBackgroundState, _action: PayloadAction<IParentCaregiver[]>) => {
        state.loading = false;
      },
    )
    .addCase(getParentCaregivers.rejected, (state: ClientFamilyBackgroundState, action: any) => {
      state.error = action.error;
      state.loading = false;
    })

    // createParentCaregiver()
    .addCase(createParentCaregiver.pending, (state: ClientFamilyBackgroundState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(createParentCaregiver.fulfilled, (state: ClientFamilyBackgroundState, action: any) => {
      state.loading = false;
      state.parentsCaregivers = state.parentsCaregivers
        ? state.parentsCaregivers
          ? state.parentsCaregivers.map((parentCaregiver, i: number) =>
              i === action.meta.arg.index ? action.payload : parentCaregiver,
            )
          : [action.payload]
        : [action.payload];
    })
    .addCase(createParentCaregiver.rejected, (state: ClientFamilyBackgroundState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })

    // updateParentCaregiver()
    .addCase(updateParentCaregiver.pending, (state: ClientFamilyBackgroundState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(updateParentCaregiver.fulfilled, (state: ClientFamilyBackgroundState, action: any) => {
      state.loading = false;

      const treatmentPlanId = action.meta.arg.treatmentPlanId;
      if (!state.parentsCaregivers || !state.parentsCaregivers[treatmentPlanId]) return;

      state.parentsCaregivers = state.parentsCaregivers.map((parentCaregiver) =>
        parentCaregiver.id === action.payload.id ? action.payload : parentCaregiver,
      );
    })
    .addCase(updateParentCaregiver.rejected, (state: ClientFamilyBackgroundState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })

    // removeParentCaregiver()
    .addCase(removeParentCaregiver.pending, (state: ClientFamilyBackgroundState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(removeParentCaregiver.fulfilled, (state: ClientFamilyBackgroundState, action: any) => {
      state.loading = false;
      state.parentsCaregivers =
        state.parentsCaregivers?.filter((pc) => pc.id !== action.meta.arg.parentCaregiverId) || [];
    })
    .addCase(removeParentCaregiver.rejected, (state: ClientFamilyBackgroundState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })
    // createSibling()
    .addCase(createSibling.pending, (state: ClientFamilyBackgroundState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(createSibling.fulfilled, (state: ClientFamilyBackgroundState, action: any) => {
      state.loading = false;
      state.siblings = state.siblings
        ? state.siblings.map((sibling, i: number) =>
            i === action.meta.arg.index ? action.payload : sibling,
          )
        : [action.payload];
    })
    .addCase(createSibling.rejected, (state: ClientFamilyBackgroundState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })

    // updateSibling()
    .addCase(updateSibling.pending, (state: ClientFamilyBackgroundState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(updateSibling.fulfilled, (state: ClientFamilyBackgroundState, action: any) => {
      state.loading = false;
      const treatmentPlanId = action.meta.arg.treatmentPlanId;
      if (!state.siblings || !state.siblings[treatmentPlanId]) return;

      state.siblings = state.siblings.map((sibling) =>
        sibling.id === action.payload.id ? action.payload : sibling,
      );
    })
    .addCase(updateSibling.rejected, (state: ClientFamilyBackgroundState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })

    // removeSibling()
    .addCase(removeSibling.pending, (state: ClientFamilyBackgroundState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(removeSibling.fulfilled, (state: ClientFamilyBackgroundState, _action: any) => {
      state.loading = false;
    })
    .addCase(removeSibling.rejected, (state: ClientFamilyBackgroundState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })

    // updateStrengths()
    .addCase(updateStrengths.pending, (state: ClientFamilyBackgroundState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(updateStrengths.fulfilled, (state: ClientFamilyBackgroundState, action: any) => {
      state.loading = false;
      state.strengths = action.payload.client_strengths;
    })
    .addCase(updateStrengths.rejected, (state: ClientFamilyBackgroundState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })

    // updateInterests()
    .addCase(updateInterests.pending, (state: ClientFamilyBackgroundState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(updateInterests.fulfilled, (state: ClientFamilyBackgroundState, action: any) => {
      state.loading = false;
      state.interests = action.payload.client_interests;
    })
    .addCase(updateInterests.rejected, (state: ClientFamilyBackgroundState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    });
}

export const clientFamilyBackgroundActions = { ...slice.actions, ...extraActions };
export const clientFamilyBackgroundReducer = slice.reducer;
